import { AxiosInstance } from "axios";
import { IVerifyResponse } from "../interfaces/IVerifyResponse";
import { ISignInCheckResponse } from "../interfaces/ISignInCheckResponse";
import { UserType } from "../enums/UserType";

export class IdentityService {
    protected apiClient: AxiosInstance;

    constructor(apiClient: AxiosInstance) {
        this.apiClient = apiClient;
    }

    async signInUser(email: string): Promise<ISignInCheckResponse> {
        try {
            const response = await this.apiClient.post(`/users/signin`, { email });
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.status === 404) {
                throw new Error('User not Found. Please sign up.');
            }
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async acceptTermsAndContinueSignIn(email: string, termsVersion: string, signupTermsVersion: string): Promise<any> {
        try {
            const response = await this.apiClient.post(`/users/signin/accept-terms`, {
                email,
                acceptTerms: true,
                termsVersion,
                signupTermsVersion,
                acceptedAtUtc: new Date().toISOString(),
                acceptedVia: 'web-signin'
            });
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.status === 404) {
                throw new Error('User not found. Please sign up.');
            }
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async verifySignInUserCode(email: string, code: string): Promise<IVerifyResponse | false> {
        try {
            const response = await this.apiClient.post(`/users/signin/verify`, { email, code });
            return response.data as IVerifyResponse;
        } catch (error: any) {
            if (error.status === 400) {
                return false;
            }
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async signUpUser(firstName: string, lastName: string, email: string, company: string, userType: UserType, referralCode: string): Promise<any> {
        try {
            const response = await this.apiClient.post(`/users/signup`, { firstName, lastName, email, company, userType, referralCode });
            return response.data;
        } catch (error: any) {
            // Handle 409 Conflict (user already has this role)
            if (error.response && error.response.status === 409) {
                throw new Error(error.response.data || 'You already have this role. Please sign in instead.');
            }
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async verifySignUpUserCode(
        firstName: string,
        lastName: string,
        email: string,
        company: string,
        userType: UserType,
        code: string,
        acceptTerms?: boolean,
        termsVersion?: string,
        signupTermsVersion?: string,
        acceptedAtUtc?: string,
        acceptedVia?: string
    ): Promise<IVerifyResponse | false> {
        try {
            const payload: any = { firstName, lastName, email, company, userType, code };

            if (acceptTerms !== undefined) {
                payload.acceptTerms = acceptTerms;
                payload.termsVersion = termsVersion;
                payload.signupTermsVersion = signupTermsVersion;
                payload.acceptedAtUtc = acceptedAtUtc;
                payload.acceptedVia = acceptedVia;
            }

            const response = await this.apiClient.post(`/users/signup/verify`, payload);
            return response.data as IVerifyResponse;
        } catch (error: any) {
            if (error.response) {
                if (error.response.status === 400) {
                    return false;
                }
                // Handle 409 Conflict (user already has this role)
                if (error.response.status === 409) {
                    throw new Error(error.response.data || 'You already have this role. Please sign in instead.');
                }
            }
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getCurrentUser(): Promise<IVerifyResponse> {
        try {
            const response = await this.apiClient.get(`/users/me`);
            return response.data as IVerifyResponse;
        } catch (error: any) {
            if (error.response && error.response.status === 401) {
                throw new Error('Not authenticated');
            }
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    //create api function to resend otp code for both sign in and sign up
    async resendOTP(email: string, context: string): Promise<any> {
        try {
            const response = await this.apiClient.post(`/users/resendOtp`, { email, context });
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.status === 404) {
                throw new Error('User not found. Please sign up.');
            }
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }
}